import { debounce } from '@/Utility/utils';
import { CircularProgress, TextField, type TextFieldProps } from '@mui/material';
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';

// this component is exactly the same <TextField /> component from material-ui
// but it has a debounce feature which only calls onChange after the user stops typing

type TextRef = {
  setInputValue: (value: string) => void;
};

type Props = {
  debounceMs?: number; // debounce time in ms, default is 500
  isLoading?: boolean;
  defaultValue?: string;
} & Omit<TextFieldProps, 'ref' | 'defaultValue'>;

const TextComponent = (
  { debounceMs, isLoading, defaultValue, ...props }: Props,
  forwardRef: React.ForwardedRef<TextRef>
) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState(defaultValue ?? '');

  const inputRef = React.useRef<HTMLDivElement | null>(null);

  const updateValueHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(false);
      props?.onChange?.(e);
    },
    [props?.onChange, setIsEditing]
  );

  const debounceOnChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      updateValueHandler(e); // callback
    }, debounceMs || 500),
    [debounce, updateValueHandler]
  );

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEditing(true);
    setInputValue(e.target.value);
    debounceOnChange(e);
  };

  useImperativeHandle(
    forwardRef,
    () => ({
      setInputValue,
    }),
    [setInputValue]
  );

  useEffect(() => {
    setInputValue(defaultValue ?? '');
  }, [defaultValue]);

  const value = React.useMemo(() => {
    if (isEditing) return inputValue;

    return props?.value ?? inputValue ?? '';
  }, [props?.value, isEditing, inputValue]);

  return (
    <TextField
      {...props}
      defaultValue={defaultValue}
      onChange={onChangeHandler}
      value={value}
      ref={inputRef}
      slotProps={{
        ...props?.slotProps,
        input: {
          ...(isLoading && {
            endAdornment: <CircularProgress size={20} />,
          }),
          ...props?.slotProps?.input,
        },
        // @deprecated - but <Autocomplete /> still uses this
        // @todo - remove this when <Autocomplete /> is updated
        ...(props?.InputProps && {
          input: {
            ...(isLoading && {
              endAdornment: <CircularProgress size={20} />,
            }),
            ...props?.InputProps,
          },
        }),
        ...(props?.inputProps && {
          htmlInput: props?.inputProps,
        }),
        ...(props?.SelectProps && {
          select: props?.SelectProps,
        }),
        ...(props?.InputLabelProps && {
          inputLabel: props?.InputLabelProps,
        }),
        ...(props?.FormHelperTextProps && {
          formHelper: props?.FormHelperTextProps,
        }),
      }}
    />
  );
};

export const useTextRef = () => {
  return React.useRef<TextRef>(null);
};

export const Text = React.forwardRef(TextComponent);
export default Text;
